/**
 * CameraRigBehaviour — the camera rig's per-frame LOGIC expressed as a Behaviour over the
 * engine API (the "camera on the scripting system" hybrid, design epic). It reads its own
 * `core.CameraRig` + `core.Camera2D` component data, resolves the tracking target via
 * `this.world.getEntityByGuid` + `this.world.getTransform`, and writes the camera's transform
 * + ortho size. Active-camera SELECTION (priority/channel/solo) and projection/viewport stay
 * engine-side per the hybrid decision.
 *
 * SCOPE: faithfully ported from `CameraRigSystem` —
 *  - Position+zoom: Follow / PositionComposer / HardLockToTarget / None + follow offset +
 *    first-frame snap + lookahead + dead/soft zone + Unity SmoothDamp + confine + lens.
 *  - Rotation: HardLookAt / RotationComposer / RotateWithFollowTarget + dutch tilt + rotation
 *    noise, in radians, written negated (renderer Y-flip) — but ONLY when rotation logic runs,
 *    so non-rotating cameras are left untouched (avoids the original's sign flip at non-zero).
 *  - Orbital: camera orbits the target (radius/height/angle + recentering + damping).
 *  - SplineDolly: dolly along a Spline2D (closest/manual/auto-scroll + damping + align).
 *  - TargetGroup: weighted center of a TargetGroup + suggested zoom (the `createSplineEvaluator`
 *    / `evaluateTargetGroup` helpers take an injected position-resolver, so they run off the
 *    engine API here and off `Level` in any legacy caller).
 * This now covers EVERY CameraRig mode — `CameraRigSystem` just selects the active rig + drives
 * this behaviour; the old per-rig math in CameraRigSystem is removed.
 */

import { Behaviour } from "./Behaviour";
import { Components } from "../ComponentTable.generated";
import {
  PositionControlMode,
  RotationControlMode,
  type CameraRig,
} from "../components/CameraRig.component";
import { createSplineEvaluator } from "../components/Spline2D.component";
import { evaluateTargetGroup } from "../components/TargetGroup.component";

const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;

interface PosResult {
  x: number;
  y: number;
  velocityX: number;
  velocityY: number;
}

export class CameraRigBehaviour extends Behaviour {
  // Per-rig runtime state (underscore-prefixed → excluded from the variable snapshot).
  private _initialized = false;
  private _velX = 0;
  private _velY = 0;
  private _prevTargetX = 0;
  private _prevTargetY = 0;
  private _lookaheadX = 0;
  private _lookaheadY = 0;
  private _noisePhase = 0;
  private _orbitAngle = 0;
  private _recenterIdleTime = 0;
  private _prevOrbitInput = 0;
  private _splineT = 0;
  private _splineVelT = 0;

  override onUpdate(deltaTime: number): void {
    const rig = this.getComponent(Components.CameraRig);
    if (!rig) return;
    const camera = this.getComponent(Components.Camera2D);

    const cur = this.transform.getPosition();
    let x = cur.x;
    let y = cur.y;
    let orthoSize = camera?.orthographicSize ?? 5;
    // `rotation` is the stored localRotationRadians (negated convention — see the write below).
    let rotation = this.transform.getRotation() * DEG2RAD;
    let rotationChanged = false;

    const target = this.getTargetPosition(rig);

    // Target-group suggested zoom.
    if (target?.suggestedSize !== undefined) {
      if (this._initialized) {
        const t = Math.min(1, 1 - Math.exp(-0.5 / deltaTime));
        orthoSize = orthoSize + (target.suggestedSize - orthoSize) * t;
      } else {
        orthoSize = target.suggestedSize;
      }
    }

    // Position control.
    const mode = rig.positionMode;
    if (mode === PositionControlMode.SplineDolly) {
      const s = this.applySplineDolly(rig, deltaTime, target);
      if (s) {
        x = s.x;
        y = s.y;
        if (rig.alignToSpline) {
          rotation = s.rotation;
          rotationChanged = true;
        }
        this._initialized = true;
      }
    } else if (target && mode === PositionControlMode.Orbital) {
      const o = this.applyOrbital(target.x, target.y, rig, deltaTime);
      x = o.x;
      y = o.y;
      this._initialized = true;
    } else if (target && mode !== PositionControlMode.None) {
      const r = this.applyPositionControl(x, y, target.x, target.y, rig, deltaTime, orthoSize);
      x = r.x;
      y = r.y;
      this._velX = r.velocityX;
      this._velY = r.velocityY;
    }

    // Rotation control (HardLookAt / RotationComposer / RotateWithFollowTarget).
    if (rig.rotationMode !== RotationControlMode.None) {
      rotation = this.applyRotation(rig, rotation, x, y, target, deltaTime);
      rotationChanged = true;
    }

    // Noise (position + rotation).
    if (rig.noiseEnabled) {
      const n = this.applyNoise(rig, deltaTime);
      x += n.x;
      y += n.y;
      if (n.rotation !== 0) {
        rotation += n.rotation;
        rotationChanged = true;
      }
    }

    // Confine to bounds.
    if (rig.confineEnabled) {
      const c = this.applyConfine(x, y, rig, deltaTime);
      x = c.x;
      y = c.y;
    }

    // Dutch tilt.
    if (rig.dutchAngle !== 0) {
      rotation += rig.dutchAngle * DEG2RAD;
      rotationChanged = true;
    }

    // Lens (ortho size) override.
    if (rig.overrideLens) {
      orthoSize = this.applyLens(orthoSize, rig, deltaTime);
    }

    this.transform.setPosition(x, y);
    // Negated to match the renderer Y-flip (CameraRigSystem: localRotationRadians = -rotation).
    // Written only when rotation logic ran, so non-rotating cameras are left untouched.
    if (rotationChanged) this.transform.setRotation(-rotation * RAD2DEG);
    if (camera) camera.orthographicSize = orthoSize;
  }

  /** Resolve a GUID to its world position via the engine API (the injected resolver). */
  private resolvePos(guid: string): { x: number; y: number } | null {
    const e = this.world.getEntityByGuid(guid);
    if (e == null) return null;
    const t = this.world.getTransform(e);
    return t ? { x: t.position.x, y: t.position.y } : null;
  }

  /** Resolve the target position: TargetGroup center (with suggested zoom) or tracking target. */
  private getTargetPosition(
    rig: CameraRig,
  ): { x: number; y: number; suggestedSize?: number } | null {
    if (rig.targetGroup) {
      const ge = this.world.getEntityByGuid(rig.targetGroup);
      if (ge != null) {
        const groupData = this.world.getComponent(ge, Components.TargetGroup);
        if (groupData) {
          const result = evaluateTargetGroup((g) => this.resolvePos(g), groupData, 16 / 9);
          if (result && result.validTargets > 0) {
            return {
              x: result.center.x,
              y: result.center.y,
              suggestedSize: rig.useGroupZoom ? result.suggestedSize : undefined,
            };
          }
        }
      }
    }
    if (!rig.trackingTarget) return null;
    return this.resolvePos(rig.trackingTarget);
  }

  /** Spline-dolly position (faithful port; spline resolved via the engine API). */
  private applySplineDolly(
    rig: CameraRig,
    deltaTime: number,
    target: { x: number; y: number } | null,
  ): { x: number; y: number; rotation: number } | null {
    if (!rig.splinePath) return null;
    const se = this.world.getEntityByGuid(rig.splinePath);
    if (se == null) return null;
    const splineData = this.world.getComponent(se, Components.Spline2D);
    if (!splineData) return null;
    const spline = createSplineEvaluator((g) => this.resolvePos(g), splineData);
    if (!spline) return null;

    const closed = splineData.closed ?? false;
    const maxT = spline.segmentCount;
    if (maxT === 0) return null;

    let targetT = this._splineT;
    switch (rig.splinePositionMode) {
      case 0: // ClosestToTarget
      case 1: // TargetForward (forward offset TODO, same as engine)
        if (target) targetT = spline.findClosestT(target.x, target.y, 200);
        break;
      case 2: // Manual
        targetT = rig.splinePosition;
        break;
      case 3: // AutoScroll
        targetT = this._splineT + (rig.autoScrollSpeed ?? 1.0) * deltaTime;
        break;
    }

    if (closed) targetT = ((targetT % maxT) + maxT) % maxT;
    else targetT = Math.max(0, Math.min(maxT, targetT));

    if (!this._initialized || (this._splineT === 0 && targetT !== 0)) {
      this._splineT = targetT;
      this._splineVelT = 0;
    }

    if (rig.splinePositionMode === 3) {
      this._splineT = targetT;
    } else {
      const smoothTime = Math.max(0.0001, rig.splineDamping);
      if (smoothTime < 0.01 || rig.autoDollySpeed <= 0) {
        this._splineT = targetT;
      } else {
        let deltaT = targetT - this._splineT;
        if (closed) {
          if (deltaT > maxT / 2) deltaT -= maxT;
          if (deltaT < -maxT / 2) deltaT += maxT;
        }
        const maxDelta = rig.autoDollySpeed * deltaTime;
        if (Math.abs(deltaT) > maxDelta && rig.autoDollySpeed > 0) {
          deltaT = Math.sign(deltaT) * maxDelta;
        }
        const omega = 2.0 / smoothTime;
        const e = omega * deltaTime;
        const exp = 1.0 / (1.0 + e + 0.48 * e * e + 0.235 * e * e * e);
        const change = this._splineT - targetT;
        const temp = (this._splineVelT + omega * change) * deltaTime;
        this._splineVelT = (this._splineVelT - omega * temp) * exp;
        this._splineT = targetT + (change + temp) * exp;
      }
    }

    if (closed) this._splineT = ((this._splineT % maxT) + maxT) % maxT;
    else this._splineT = Math.max(0, Math.min(maxT, this._splineT));

    const pos = spline.evaluate(this._splineT);
    const rotation = spline.evaluateRotation(this._splineT);
    return {
      x: pos.x + (rig.followOffset?.x ?? 0),
      y: pos.y + (rig.followOffset?.y ?? 0),
      rotation,
    };
  }

  // Faithful port of CameraRigSystem.applyPositionControl (Follow/PositionComposer/HardLock).
  private applyPositionControl(
    currentX: number,
    currentY: number,
    targetX: number,
    targetY: number,
    rig: CameraRig,
    deltaTime: number,
    orthoSize: number,
  ): PosResult {
    targetX += rig.followOffset?.x ?? 0;
    targetY += rig.followOffset?.y ?? 0;

    // First frame: snap to target to avoid initial jerk.
    if (!this._initialized) {
      this._initialized = true;
      return { x: targetX, y: targetY, velocityX: 0, velocityY: 0 };
    }

    if (rig.positionMode === PositionControlMode.HardLockToTarget) {
      return { x: targetX, y: targetY, velocityX: 0, velocityY: 0 };
    }

    // Lookahead (predict target velocity).
    let lookaheadOffsetX = 0;
    let lookaheadOffsetY = 0;
    if (rig.lookaheadEnabled && rig.lookaheadTime > 0) {
      const velX = (targetX - this._prevTargetX) / Math.max(deltaTime, 0.001);
      const velY = rig.lookaheadIgnoreY
        ? 0
        : (targetY - this._prevTargetY) / Math.max(deltaTime, 0.001);
      this._prevTargetX = targetX;
      this._prevTargetY = targetY;
      const targetLookaheadX = velX * rig.lookaheadTime;
      const targetLookaheadY = velY * rig.lookaheadTime;
      const smoothing = Math.max(0.1, rig.lookaheadSmoothing);
      const t = 1 - Math.exp(-deltaTime * smoothing);
      this._lookaheadX += (targetLookaheadX - this._lookaheadX) * t;
      this._lookaheadY += (targetLookaheadY - this._lookaheadY) * t;
      lookaheadOffsetX = this._lookaheadX;
      lookaheadOffsetY = this._lookaheadY;
    }

    let dx = targetX + lookaheadOffsetX - currentX;
    let dy = targetY + lookaheadOffsetY - currentY;
    let effectiveDamping = rig.positionDamping;

    // Dead zone + soft zone (PositionComposer only).
    if (rig.positionMode === PositionControlMode.PositionComposer) {
      const aspectRatio = 16 / 9; // matches CameraRigSystem's TODO default
      const viewWidth = orthoSize * 2 * aspectRatio;
      const viewHeight = orthoSize * 2;
      const deadZoneCenterX = rig.deadZoneOffsetX * viewWidth;
      const deadZoneCenterY = rig.deadZoneOffsetY * viewHeight;
      const deadZoneHalfW = rig.deadZoneWidth * viewWidth * 0.5;
      const deadZoneHalfH = rig.deadZoneHeight * viewHeight * 0.5;
      const relX = dx - deadZoneCenterX;
      const relY = dy - deadZoneCenterY;
      const softZoneHalfW = rig.softZoneWidth * viewWidth * 0.5;
      const softZoneHalfH = rig.softZoneHeight * viewHeight * 0.5;

      if (Math.abs(relX) < deadZoneHalfW) {
        dx = deadZoneCenterX;
      } else {
        const pushX = relX > 0 ? relX - deadZoneHalfW : relX + deadZoneHalfW;
        if (softZoneHalfW > deadZoneHalfW) {
          const softZoneT = Math.min(1, (Math.abs(relX) - deadZoneHalfW) / (softZoneHalfW - deadZoneHalfW));
          effectiveDamping = rig.positionDamping * (1 - softZoneT * 0.8);
        }
        dx = deadZoneCenterX + pushX;
      }
      if (Math.abs(relY) < deadZoneHalfH) {
        dy = deadZoneCenterY;
      } else {
        const pushY = relY > 0 ? relY - deadZoneHalfH : relY + deadZoneHalfH;
        if (softZoneHalfH > deadZoneHalfH) {
          const softZoneT = Math.min(1, (Math.abs(relY) - deadZoneHalfH) / (softZoneHalfH - deadZoneHalfH));
          effectiveDamping = Math.min(effectiveDamping, rig.positionDamping * (1 - softZoneT * 0.8));
        }
        dy = deadZoneCenterY + pushY;
      }
    }

    const smoothTime = Math.max(0.0001, effectiveDamping);
    if (smoothTime < 0.01) {
      return { x: currentX + dx, y: currentY + dy, velocityX: 0, velocityY: 0 };
    }

    // Teleport (instant catch-up) if the target jumped far.
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > 50) {
      return { x: targetX, y: targetY, velocityX: 0, velocityY: 0 };
    }

    // Unity-style critically-damped SmoothDamp with distance-scaled max speed.
    const omega = 2.0 / smoothTime;
    const e = omega * deltaTime;
    const exp = 1.0 / (1.0 + e + 0.48 * e * e + 0.235 * e * e * e);
    const maxSpeed = Math.max(50, dist * 3);
    const changeX = currentX - targetX;
    const changeY = currentY - targetY;
    const maxChange = maxSpeed * smoothTime;
    const changeMag = Math.sqrt(changeX * changeX + changeY * changeY);
    const clampedChangeX = changeMag > maxChange ? (changeX * maxChange) / changeMag : changeX;
    const clampedChangeY = changeMag > maxChange ? (changeY * maxChange) / changeMag : changeY;
    const tempX = (this._velX + omega * clampedChangeX) * deltaTime;
    const tempY = (this._velY + omega * clampedChangeY) * deltaTime;
    let newVelX = (this._velX - omega * tempX) * exp;
    let newVelY = (this._velY - omega * tempY) * exp;
    const velMag = Math.sqrt(newVelX * newVelX + newVelY * newVelY);
    if (velMag > maxSpeed) {
      newVelX = (newVelX * maxSpeed) / velMag;
      newVelY = (newVelY * maxSpeed) / velMag;
    }
    return {
      x: targetX + (clampedChangeX + tempX) * exp,
      y: targetY + (clampedChangeY + tempY) * exp,
      velocityX: newVelX,
      velocityY: newVelY,
    };
  }

  private applyNoise(rig: CameraRig, deltaTime: number): { x: number; y: number; rotation: number } {
    this._noisePhase += deltaTime * rig.noiseFrequency;
    const phase = this._noisePhase;
    const phi = 1.618033988749;
    const noiseX =
      Math.sin(phase) * 0.5 + Math.sin(phase * phi) * 0.3 + Math.sin(phase * phi * phi) * 0.2;
    const noiseY =
      Math.sin(phase * 0.7 + 1.5) * 0.5 +
      Math.sin(phase * phi * 0.7 + 2.3) * 0.3 +
      Math.sin(phase * phi * phi * 0.7 + 3.1) * 0.2;
    const rotation =
      rig.noiseRotationAmplitude > 0
        ? Math.sin(phase * 0.5 + 0.7) * rig.noiseRotationAmplitude * DEG2RAD
        : 0;
    return { x: noiseX * rig.noiseAmplitude, y: noiseY * rig.noiseAmplitude, rotation };
  }

  // ── Rotation (radians; faithful port of CameraRigSystem) ──────────────────

  /** Resolve the rotation for this frame per `rig.rotationMode`. */
  private applyRotation(
    rig: CameraRig,
    currentRotation: number,
    camX: number,
    camY: number,
    target: { x: number; y: number } | null,
    deltaTime: number,
  ): number {
    if (rig.rotationMode === RotationControlMode.RotateWithFollowTarget) {
      // Match the tracking target's rotation (with damping).
      if (!rig.trackingTarget) return currentRotation;
      const e = this.world.getEntityByGuid(rig.trackingTarget);
      if (e == null) return currentRotation;
      const tt = this.world.getTransform(e);
      if (!tt) return currentRotation;
      const targetRotation = tt.rotation * DEG2RAD;
      if (rig.rotationDamping > 0) {
        const t = 1 - Math.exp(-rig.rotationDamping * deltaTime);
        return currentRotation + this.angleDiff(targetRotation, currentRotation) * t;
      }
      return targetRotation;
    }

    // HardLookAt / RotationComposer — look at the look-at target (or the tracking position).
    const look = this.getLookAtPosition(rig, target);
    if (!look) return currentRotation;

    if (rig.rotationMode === RotationControlMode.HardLookAt) {
      return Math.atan2(look.y - camY, look.x - camX);
    }
    if (rig.rotationMode === RotationControlMode.RotationComposer) {
      const targetRotation = Math.atan2(look.y - camY, look.x - camX);
      const t = 1 - Math.exp(-rig.rotationDamping * deltaTime);
      return currentRotation + this.angleDiff(targetRotation, currentRotation) * t;
    }
    return currentRotation;
  }

  private getLookAtPosition(
    rig: CameraRig,
    trackingPos: { x: number; y: number } | null,
  ): { x: number; y: number } | null {
    if (!rig.lookAtTarget || rig.lookAtTarget === "__separate__") return trackingPos;
    const e = this.world.getEntityByGuid(rig.lookAtTarget);
    if (e == null) return trackingPos;
    const t = this.world.getTransform(e);
    return t ? { x: t.position.x, y: t.position.y } : trackingPos;
  }

  /** Shortest signed angle difference (radians). */
  private angleDiff(target: number, current: number): number {
    let diff = target - current;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    return diff;
  }

  // ── Orbital (degrees; faithful port + the missing init/damping the original dropped) ──

  private applyOrbital(
    targetX: number,
    targetY: number,
    rig: CameraRig,
    deltaTime: number,
  ): { x: number; y: number } {
    const radius = rig.orbitRadius ?? 5;
    const height = rig.orbitHeight ?? 0;
    const damping = rig.orbitDamping ?? 0.3;

    if (!this._initialized) {
      this._orbitAngle = rig.orbitAngle ?? 0;
      this._recenterIdleTime = 0;
    }

    let targetAngle = rig.orbitAngle ?? 0;

    if (rig.recenterEnabled) {
      const inputDelta = Math.abs(targetAngle - this._prevOrbitInput);
      if (inputDelta > 0.5) this._recenterIdleTime = 0;
      else this._recenterIdleTime += deltaTime;
      this._prevOrbitInput = targetAngle;

      const waitTime = rig.recenterWaitTime ?? 1;
      if (this._recenterIdleTime >= waitTime) {
        const recenterProgress = Math.min(1, (this._recenterIdleTime - waitTime) / (rig.recenterTime ?? 2));
        const eased =
          recenterProgress < 0.5
            ? 2 * recenterProgress * recenterProgress
            : 1 - Math.pow(-2 * recenterProgress + 2, 2) / 2;
        targetAngle = this.lerpAngle(targetAngle, rig.recenterAngle ?? 0, eased);
      }
    }

    if (damping > 0 && this._initialized) {
      const t = 1 - Math.exp(-deltaTime / damping);
      this._orbitAngle = this.lerpAngle(this._orbitAngle, targetAngle, t);
    } else {
      this._orbitAngle = targetAngle;
    }

    const angleRad = this._orbitAngle * DEG2RAD;
    return {
      x: targetX + Math.cos(angleRad) * radius,
      y: targetY + Math.sin(angleRad) * radius + height,
    };
  }

  /** Lerp between two angles (degrees), shortest path. */
  private lerpAngle(from: number, to: number, t: number): number {
    let diff = ((to - from + 180) % 360) - 180;
    if (diff < -180) diff += 360;
    return from + diff * t;
  }

  private applyConfine(
    x: number,
    y: number,
    rig: CameraRig,
    deltaTime: number,
  ): { x: number; y: number } {
    const clampedX = Math.max(rig.boundsMinX, Math.min(rig.boundsMaxX, x));
    const clampedY = Math.max(rig.boundsMinY, Math.min(rig.boundsMaxY, y));
    if (rig.confineMode === 1) return { x: clampedX, y: clampedY };
    const outX = clampedX - x;
    const outY = clampedY - y;
    if (outX === 0 && outY === 0) return { x, y };
    const omega = 2.0 / Math.max(0.01, rig.confineDamping);
    const exp = Math.exp(-omega * deltaTime);
    return { x: x + outX * (1 - exp), y: y + outY * (1 - exp) };
  }

  private applyLens(currentSize: number, rig: CameraRig, deltaTime: number): number {
    if (rig.lensDamping <= 0) return rig.targetOrthoSize;
    const t = 1 - Math.exp(-deltaTime / rig.lensDamping);
    return currentSize + (rig.targetOrthoSize - currentSize) * t;
  }
}
